import React, { useState, useEffect, useMemo } from "react";
import cotacaoService from "../../services/cotacaoService";
import estoqueService from "../../services/estoqueService";
import fornecedoresService, { STORAGE_OPEN_FORNECEDOR } from "../../services/fornecedoresService";
import { STORAGE_OPEN_PEDIDO } from "../../services/pedidosService";
import { useAuth } from "../../contexts/AuthContext";
import ItemEstoqueAutocomplete from "../../components/estoque/ItemEstoqueAutocomplete";
import {
  itemIdUmBase, itemIdUmOp, itemIdUmCompra,
  unidadesOperacionais,
} from "../../utils/estoqueUm";

const fmtMoeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtQtd = (v) => Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });
const num = (v) => parseFloat(v) || 0;

const LINHA_EMPTY = { idItem: "", quantidade: "", idUnidadeInformada: "", prazoDesejado: "", observacao: "" };

const umPadraoItem = (itens, idItem) => {
  const it = (itens || []).find((i) => String(i.id) === String(idItem));
  if (!it) return "";
  return itemIdUmCompra(it) || itemIdUmOp(it) || itemIdUmBase(it) || "";
};

const umsDoItem = (unidades, itens, idItem) => {
  const it = (itens || []).find((i) => String(i.id) === String(idItem));
  const idBase = itemIdUmBase(it) || "";
  return unidadesOperacionais(unidades, idBase);
};

export default function CotacaoModal({
  show, onClose, onSaved, onCreated, cotacaoId, idCondominio, canEdit, saving, setSaving, onNavigate,
}) {
  const { podeEditar } = useAuth();
  const canEditFornecedor = podeEditar("fornecedores");
  const [aba, setAba] = useState("geral");
  const [error, setError] = useState(null);
  const [cotacao, setCotacao] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [dtLimite, setDtLimite] = useState("");
  const [observacao, setObservacao] = useState("");
  const [linhas, setLinhas] = useState([{ ...LINHA_EMPTY }]);
  const [fornecedoresSel, setFornecedoresSel] = useState([]);
  const [itensEstoque, setItensEstoque] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [novaLinha, setNovaLinha] = useState({ ...LINHA_EMPTY });
  const [fornRespId, setFornRespId] = useState("");
  const [respLinhas, setRespLinhas] = useState([]);
  const [mapa, setMapa] = useState(null);
  const [escolhas, setEscolhas] = useState({});
  const [pedidosGerados, setPedidosGerados] = useState([]);
  const [gerandoPedidos, setGerandoPedidos] = useState(false);
  const [enviandoLembretes, setEnviandoLembretes] = useState(false);
  const [imprimindoMapa, setImprimindoMapa] = useState(false);
  const [sugestaoDiasCobertura, setSugestaoDiasCobertura] = useState("0");
  const [sugestaoDiasHistorico, setSugestaoDiasHistorico] = useState("90");
  const [carregandoSugestao, setCarregandoSugestao] = useState(false);

  const readOnly = !canEdit || (cotacao && ["Encerrada", "Cancelada"].includes(cotacao.status));
  const mapaBloqueado = readOnly || pedidosGerados.length > 0;
  const temRespostas = (cotacao?.respostas || []).length > 0;

  const resetForm = () => {
    setAba("geral");
    setError(null);
    setCotacao(null);
    setTitulo("");
    setDescricao("");
    setDtLimite("");
    setObservacao("");
    setLinhas([{ ...LINHA_EMPTY }]);
    setFornecedoresSel([]);
    setNovaLinha({ ...LINHA_EMPTY });
    setFornRespId("");
    setRespLinhas([]);
    setMapa(null);
    setEscolhas({});
    setPedidosGerados([]);
  };

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }
    estoqueService.listarItens({ idCondominio, ativo: "1", limit: 500 })
      .then((r) => setItensEstoque(r.data || [])).catch(() => {});
    estoqueService.listarUnidadesMedida({ idCondominio })
      .then((r) => setUnidades(r.data || [])).catch(() => {});
    fornecedoresService.listar({ idCondominio, situacao: "ativo", limit: 300 })
      .then((r) => setFornecedores(r.data || [])).catch(() => {});

    if (cotacaoId) {
      cotacaoService.buscarPorId(cotacaoId).then((r) => {
        const c = r.data;
        setCotacao(c);
        setTitulo(c.titulo || "");
        setDescricao(c.descricao || "");
        setDtLimite((c.dtLimite || c.dtlimite || "").split("T")[0]);
        setObservacao(c.observacao || "");
        setLinhas((c.itens || []).map((i) => ({
          id: i.id,
          idItem: String(i.iditem || i.idItem || ""),
          quantidade: String(i.quantidade ?? ""),
          idUnidadeInformada: String(i.idunidadeinformada || i.idUnidadeInformada || ""),
          prazoDesejado: (i.prazodesejado || i.prazoDesejado || "").split("T")[0],
          observacao: i.observacao || "",
        })));
        setFornecedoresSel((c.fornecedores || []).map((f) => String(f.idfornecedor || f.idFornecedor)));
      }).catch((e) => setError(e.message));
    } else {
      resetForm();
    }
  }, [show, cotacaoId, idCondominio]);

  const carregarMapa = async () => {
    if (!cotacaoId) return;
    try {
      const [rMapa, rEscolhas, rPedidos] = await Promise.all([
        cotacaoService.mapa(cotacaoId),
        cotacaoService.listarEscolhas(cotacaoId),
        cotacaoService.listarPedidos(cotacaoId),
      ]);
      setMapa(rMapa.data);
      const esc = {};
      (rEscolhas.data || []).forEach((e) => {
        esc[e.idCotacaoItem] = String(e.idCotFornecedor);
      });
      setEscolhas(esc);
      setPedidosGerados(rPedidos.data || []);
    } catch (e) { setError(e.message); }
  };

  useEffect(() => {
    if (show && aba === "mapa" && cotacaoId) carregarMapa();
  }, [show, aba, cotacaoId]);

  const linhasValidas = useMemo(
    () => linhas.filter((l) => l.idItem && num(l.quantidade) > 0),
    [linhas]
  );

  const payloadBase = () => ({
    idCondominio,
    titulo: titulo.trim(),
    descricao,
    dtLimite: dtLimite || null,
    observacao,
    itens: linhasValidas.map((l, idx) => ({
      id: l.id,
      idItem: l.idItem,
      quantidade: num(l.quantidade),
      idUnidadeInformada: l.idUnidadeInformada || null,
      prazoDesejado: l.prazoDesejado || null,
      observacao: l.observacao || null,
      ordem: idx + 1,
    })),
    fornecedores: fornecedoresSel.map((id) => ({ idFornecedor: id })),
  });

  const salvar = async () => {
    if (!titulo.trim()) { setError("Título é obrigatório."); return; }
    if (!linhasValidas.length) { setError("Informe ao menos um item."); return; }
    setSaving(true); setError(null);
    try {
      if (cotacaoId) {
        await cotacaoService.atualizar(cotacaoId, payloadBase());
        onSaved?.();
        onClose?.();
      } else {
        const r = await cotacaoService.criar(payloadBase());
        setCotacao(r.data);
        onCreated?.(r.data?.id);
        setAba("fornecedores");
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addLinha = () => {
    if (!novaLinha.idItem || num(novaLinha.quantidade) <= 0) return;
    setLinhas((p) => [...p, { ...novaLinha }]);
    setNovaLinha({ ...LINHA_EMPTY });
  };

  const toggleFornecedor = (id) => {
    const s = String(id);
    setFornecedoresSel((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));
  };

  const abrirResposta = (idCotForn) => {
    setFornRespId(String(idCotForn));
    const cf = (cotacao?.fornecedores || []).find((f) => String(f.id) === String(idCotForn));
    const respostasCf = (cotacao?.respostas || []).filter(
      (r) => String(r.idcotacaofornecedor) === String(idCotForn)
    );
    const mapR = new Map(respostasCf.map((r) => [String(r.idcotacaoitem), r]));
    setRespLinhas((cotacao?.itens || []).map((i) => {
      const r = mapR.get(String(i.id));
      return {
        idCotacaoItem: i.id,
        cdItem: i.cditem,
        nmItem: i.nmitem,
        quantidade: i.quantidade,
        siglaUm: i.siglaunidadeinformada || i.siglaunidadebase,
        disponivel: r ? r.disponivel !== 0 : true,
        vlrUnitario: r?.vlrunitario != null ? String(r.vlrunitario) : "",
        prazoEntrega: (r?.prazoentrega || "").split("T")[0],
        observacao: r?.observacao || "",
      };
    }));
    setAba("respostas");
  };

  const salvarResposta = async () => {
    if (!cotacaoId || !fornRespId) return;
    setSaving(true); setError(null);
    try {
      const r = await cotacaoService.salvarResposta(cotacaoId, fornRespId, {
        linhas: respLinhas.map((l) => ({
          idCotacaoItem: l.idCotacaoItem,
          disponivel: l.disponivel ? 1 : 0,
          vlrUnitario: l.disponivel && l.vlrUnitario !== "" ? num(l.vlrUnitario) : null,
          prazoEntrega: l.disponivel ? (l.prazoEntrega || null) : null,
          observacao: l.observacao || null,
        })),
        statusFornecedor: "Respondida",
      });
      setCotacao(r.data);
      setOkMsgInterno("Resposta salva.");
      await carregarMapa();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const [okMsgInterno, setOkMsgInterno] = useState(null);
  const [enviandoConvites, setEnviandoConvites] = useState(false);

  const recarregarCotacao = async () => {
    if (!cotacaoId) return;
    const r = await cotacaoService.buscarPorId(cotacaoId);
    setCotacao(r.data);
    return r.data;
  };

  const emailCadastroFornecedor = (idFornecedor) => {
    const f = fornecedores.find((x) => String(x.id) === String(idFornecedor));
    return (f?.email || "").trim();
  };

  const fornecedoresSemEmail = useMemo(() => {
    const ids = cotacaoId
      ? (cotacao?.fornecedores || []).map((cf) => cf.idfornecedor || cf.idFornecedor)
      : fornecedoresSel;
    return ids
      .map((id) => fornecedores.find((f) => String(f.id) === String(id)))
      .filter((f) => f && !emailCadastroFornecedor(f.id));
  }, [cotacao, fornecedoresSel, fornecedores, cotacaoId]);

  const fornecedoresPendentes = useMemo(() => (
    (cotacao?.fornecedores || []).filter((cf) => cf.status === "Aguardando")
  ), [cotacao]);

  const enviarLembretes = async (idCotFornecedor = null) => {
    if (!cotacaoId) return;
    const alvo = idCotFornecedor ? "este fornecedor" : "todos os pendentes";
    if (!window.confirm(`Enviar lembrete por e-mail para ${alvo}?`)) return;
    setEnviandoLembretes(true);
    setError(null);
    setOkMsgInterno(null);
    try {
      const body = idCotFornecedor ? { idCotFornecedor } : {};
      const r = await cotacaoService.enviarLembretes(cotacaoId, body);
      const { enviados = [], semEmail = [], falhas = [], message } = r.data || {};
      await recarregarCotacao();
      const partes = [message];
      if (semEmail.length) {
        partes.push(`${semEmail.length} sem e-mail: ${semEmail.map((s) => s.nmFornecedor).join(", ")}.`);
      }
      if (falhas.length) {
        partes.push(`Falhas: ${falhas.map((f) => `${f.email} (${f.erro})`).join("; ")}.`);
      }
      setOkMsgInterno(partes.filter(Boolean).join(" "));
    } catch (e) { setError(e.message); }
    finally { setEnviandoLembretes(false); }
  };

  const imprimirMapaPdf = async () => {
    if (!cotacaoId) return;
    setImprimindoMapa(true); setError(null);
    try {
      const r = await cotacaoService.imprimirMapa(cotacaoId);
      const w = window.open("", "_blank", "width=900,height=900");
      if (!w) {
        setError("Pop-up bloqueado. Permita pop-ups para imprimir o mapa.");
        return;
      }
      w.document.write(r.html);
      w.document.close();
    } catch (e) { setError(e.message); }
    finally { setImprimindoMapa(false); }
  };

  const aplicarSugestaoEstoque = async () => {
    if (!idCondominio) return;
    setCarregandoSugestao(true); setError(null);
    try {
      const r = await cotacaoService.sugestaoItens({
        idCondominio,
        diasCobertura: sugestaoDiasCobertura,
        diasHistorico: sugestaoDiasHistorico,
        somenteNecessarios: "1",
      });
      const sugeridos = r.data?.itens || [];
      if (!sugeridos.length) {
        setOkMsgInterno("Nenhum item com necessidade de reposição no momento.");
        return;
      }
      setLinhas((prev) => {
        const out = [...prev.filter((l) => l.idItem && num(l.quantidade) > 0)];
        const mapExistentes = new Map(out.map((l) => [String(l.idItem), l]));
        for (const s of sugeridos) {
          const key = String(s.idItem);
          const um = s.idUnidadeInformada ? String(s.idUnidadeInformada) : umPadraoItem(itensEstoque, s.idItem);
          if (mapExistentes.has(key)) {
            const ex = mapExistentes.get(key);
            ex.quantidade = String(s.quantidade);
            if (um) ex.idUnidadeInformada = um;
          } else {
            out.push({
              idItem: key,
              quantidade: String(s.quantidade),
              idUnidadeInformada: um ? String(um) : "",
              prazoDesejado: "",
              observacao: s.motivo || "",
            });
          }
        }
        return out.length ? out : [{ ...LINHA_EMPTY }];
      });
      setOkMsgInterno(`${sugeridos.length} item(ns) sugerido(s) pelo estoque adicionado(s)/atualizado(s).`);
    } catch (e) { setError(e.message); }
    finally { setCarregandoSugestao(false); }
  };

  const enviarConvites = async (idCotFornecedor = null) => {
    if (!cotacaoId) return;
    const alvo = idCotFornecedor ? "este fornecedor" : "todos os fornecedores com e-mail";
    if (!window.confirm(`Enviar convite por e-mail para ${alvo}?`)) return;
    setEnviandoConvites(true);
    setError(null);
    setOkMsgInterno(null);
    try {
      const body = idCotFornecedor ? { idCotFornecedor } : {};
      const r = await cotacaoService.enviarConvites(cotacaoId, body);
      const { enviados = [], semEmail = [], falhas = [], message } = r.data || {};
      await recarregarCotacao();
      const partes = [message];
      if (semEmail.length) {
        partes.push(
          `${semEmail.length} sem e-mail no cadastro: ${semEmail.map((s) => s.nmFornecedor).join(", ")}.`
        );
      }
      if (falhas.length) {
        partes.push(`Falhas: ${falhas.map((f) => `${f.email} (${f.erro})`).join("; ")}.`);
      }
      setOkMsgInterno(partes.filter(Boolean).join(" "));
      if (semEmail.length && !enviados.length) {
        setError("Nenhum convite enviado — cadastre o e-mail do fornecedor ou selecione outro.");
      }
    } catch (e) { setError(e.message); }
    finally { setEnviandoConvites(false); }
  };

  const fmtDtEnvio = (dt) => {
    if (!dt) return null;
    try {
      return new Date(dt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    } catch { return dt; }
  };

  const abrirCadastroFornecedor = (idFornecedor) => {
    if (!onNavigate || !canEditFornecedor) return;
    sessionStorage.setItem(STORAGE_OPEN_FORNECEDOR, String(idFornecedor));
    onNavigate("fornecedores");
  };

  const alterarStatus = async (status) => {
    if (!cotacaoId || !window.confirm(`Alterar status para ${status}?`)) return;
    setSaving(true);
    try {
      const r = await cotacaoService.alterarStatus(cotacaoId, status);
      setCotacao(r.data);
      onSaved?.();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const celulaMapa = (idItem, idCotForn) => {
    if (!mapa) return null;
    return mapa.respostas.find(
      (r) => String(r.idCotacaoItem) === String(idItem)
        && String(r.idCotFornecedor) === String(idCotForn)
        && r.disponivel && r.vlrUnitario > 0
    );
  };

  const menoresPorItem = useMemo(() => {
    if (!mapa) return {};
    const out = {};
    for (const it of mapa.itens || []) {
      const precos = (mapa.respostas || [])
        .filter((r) => String(r.idCotacaoItem) === String(it.id) && r.disponivel && r.vlrUnitario > 0)
        .map((r) => r.vlrUnitario);
      if (precos.length) out[it.id] = Math.min(...precos);
    }
    return out;
  }, [mapa]);

  const previewPedidos = useMemo(() => {
    if (!mapa) return [];
    const grupos = {};
    for (const it of mapa.itens || []) {
      const idCotForn = escolhas[it.id];
      if (!idCotForn) continue;
      const c = celulaMapa(it.id, idCotForn);
      if (!c) continue;
      const f = (mapa.fornecedores || []).find((x) => String(x.id) === String(idCotForn));
      const key = String(f?.idFornecedor || idCotForn);
      if (!grupos[key]) {
        grupos[key] = {
          idCotFornecedor: idCotForn,
          nmFornecedor: f?.nmFornecedor || "Fornecedor",
          itens: [],
          total: 0,
        };
      }
      const sub = num(it.quantidade) * num(c.vlrUnitario);
      grupos[key].itens.push({ ...it, vlrUnitario: c.vlrUnitario, subtotal: sub });
      grupos[key].total += sub;
    }
    return Object.values(grupos);
  }, [mapa, escolhas]);

  const qtdItensMapa = mapa?.itens?.length || 0;
  const qtdEscolhas = Object.keys(escolhas).length;
  const escolhasCompletas = qtdItensMapa > 0 && qtdEscolhas >= qtdItensMapa;

  const aplicarMenorPreco = async () => {
    if (!cotacaoId) return;
    setSaving(true); setError(null);
    try {
      const r = await cotacaoService.autoMenorPreco(cotacaoId);
      setOkMsgInterno(r.message || "Menor preço aplicado.");
      await carregarMapa();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const salvarEscolhasMapa = async () => {
    if (!cotacaoId) return;
    const lista = Object.entries(escolhas).map(([idCotacaoItem, idCotFornecedor]) => ({
      idCotacaoItem: Number(idCotacaoItem),
      idCotFornecedor: Number(idCotFornecedor),
    }));
    if (!lista.length) { setError("Selecione ao menos um vencedor."); return; }
    setSaving(true); setError(null);
    try {
      const r = await cotacaoService.salvarEscolhas(cotacaoId, lista);
      setOkMsgInterno(r.message || "Escolhas salvas.");
      await carregarMapa();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const gerarPedidos = async () => {
    if (!cotacaoId || !escolhasCompletas) return;
    const qtd = previewPedidos.length;
    if (!window.confirm(`Gerar ${qtd} pedido(s) em rascunho e encerrar a cotação?`)) return;
    setGerandoPedidos(true); setError(null);
    try {
      const escolhasPayload = Object.entries(escolhas).map(([idCotacaoItem, idCotFornecedor]) => ({
        idCotacaoItem: Number(idCotacaoItem),
        idCotFornecedor: Number(idCotFornecedor),
      }));
      const r = await cotacaoService.gerarPedidos(cotacaoId, {
        escolhas: escolhasPayload,
        usarEscolhasSalvas: false,
        encerrarCotacao: true,
      });
      setCotacao(r.data?.cotacao || cotacao);
      setPedidosGerados(r.data?.pedidos || []);
      setOkMsgInterno(r.message || "Pedidos gerados.");
      onSaved?.();
    } catch (e) { setError(e.message); }
    finally { setGerandoPedidos(false); }
  };

  const abrirPedido = (idPedido) => {
    if (!onNavigate) return;
    sessionStorage.setItem(STORAGE_OPEN_PEDIDO, String(idPedido));
    onNavigate("pedidos");
  };

  if (!show) return null;

  const tabs = [
    { id: "geral", label: "Geral" },
    { id: "itens", label: "Itens" },
    { id: "fornecedores", label: "Fornecedores" },
    ...(cotacaoId ? [{ id: "respostas", label: "Respostas" }, { id: "mapa", label: "Mapa" }] : []),
  ];

  return (
    <div className="modal show d-block" style={{ background: "rgba(0,0,0,.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {cotacaoId
                ? <>Cotação <code className="small">{cotacao?.nrCotacao || cotacao?.nrcotacao}</code></>
                : "Nova cotação"}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 small">{error}</div>}
            {okMsgInterno && (
              <div className="alert alert-success py-2 small">{okMsgInterno}</div>
            )}

            <ul className="nav nav-tabs mb-3">
              {tabs.map((t) => (
                <li key={t.id} className="nav-item">
                  <button type="button" className={`nav-link ${aba === t.id ? "active" : ""}`}
                    onClick={() => setAba(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>

            {aba === "geral" && (
              <div className="row g-2">
                <div className="col-md-8">
                  <label className="form-label small">Título *</label>
                  <input className="form-control form-control-sm" value={titulo}
                    disabled={readOnly} onChange={(e) => setTitulo(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Data limite resposta</label>
                  <input type="date" className="form-control form-control-sm" value={dtLimite}
                    disabled={readOnly} onChange={(e) => setDtLimite(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label small">Descrição</label>
                  <textarea className="form-control form-control-sm" rows={2} value={descricao}
                    disabled={readOnly} onChange={(e) => setDescricao(e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label small">Observação</label>
                  <textarea className="form-control form-control-sm" rows={2} value={observacao}
                    disabled={readOnly} onChange={(e) => setObservacao(e.target.value)} />
                </div>
                {cotacao && (
                  <div className="col-12">
                    <span className="badge bg-secondary me-2">Status: {cotacao.status}</span>
                    {canEdit && cotacao.status !== "Encerrada" && cotacao.status !== "Cancelada" && (
                      <>
                        <button type="button" className="btn btn-outline-primary btn-sm me-1"
                          disabled={saving} onClick={() => alterarStatus("EmAnalise")}>
                          Em análise
                        </button>
                        <button type="button" className="btn btn-outline-success btn-sm me-1"
                          disabled={saving} onClick={() => alterarStatus("Encerrada")}>
                          Encerrar
                        </button>
                        <button type="button" className="btn btn-outline-danger btn-sm"
                          disabled={saving} onClick={() => alterarStatus("Cancelada")}>
                          Cancelar
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {aba === "itens" && (
              <div>
                {!readOnly && !temRespostas && (
                  <div className="p-2 rounded mb-3 small" style={{ background: "var(--bg-highlight)" }}>
                    <div className="fw-semibold mb-2">
                      <i className="bi bi-box-seam me-1" />
                      Sugestão a partir do estoque
                    </div>
                    <div className="row g-2 align-items-end">
                      <div className="col-auto">
                        <label className="form-label small mb-0">Dias cobertura</label>
                        <input type="number" min="0" className="form-control form-control-sm" style={{ width: 90 }}
                          value={sugestaoDiasCobertura}
                          onChange={(e) => setSugestaoDiasCobertura(e.target.value)} />
                      </div>
                      <div className="col-auto">
                        <label className="form-label small mb-0">Histórico (dias)</label>
                        <input type="number" min="1" max="365" className="form-control form-control-sm" style={{ width: 90 }}
                          value={sugestaoDiasHistorico}
                          onChange={(e) => setSugestaoDiasHistorico(e.target.value)} />
                      </div>
                      <div className="col-auto">
                        <button type="button" className="btn btn-outline-primary btn-sm"
                          disabled={carregandoSugestao || saving}
                          onClick={aplicarSugestaoEstoque}>
                          {carregandoSugestao ? "Carregando…" : "Carregar itens sugeridos"}
                        </button>
                      </div>
                    </div>
                    <p className="text-muted mb-0 mt-1">
                      Itens abaixo do mínimo ou com consumo recente — mesma lógica da reposição de estoque.
                    </p>
                  </div>
                )}
                {temRespostas && (
                  <p className="small text-warning">Itens bloqueados para edição — já existem respostas.</p>
                )}
                <div className="table-responsive mb-2">
                  <table className="table table-sm">
                    <thead>
                      <tr><th>Item</th><th>Qtd</th><th>UM</th><th>Prazo desejado</th><th /></tr>
                    </thead>
                    <tbody>
                      {linhas.map((lin, idx) => (
                        <tr key={lin.id || idx}>
                          <td>
                            <code className="small">
                              {itensEstoque.find((i) => String(i.id) === String(lin.idItem))?.cdItem || "—"}
                            </code>
                            {" "}
                            {itensEstoque.find((i) => String(i.id) === String(lin.idItem))?.nmItem || ""}
                          </td>
                          <td>{fmtQtd(lin.quantidade)}</td>
                          <td className="small text-muted">
                            {unidades.find((u) => String(u.id) === String(lin.idUnidadeInformada))?.sigla || "—"}
                          </td>
                          <td>{lin.prazoDesejado || "—"}</td>
                          <td className="text-end">
                            {!readOnly && !temRespostas && linhas.length > 1 && (
                              <button type="button" className="btn btn-sm btn-outline-danger py-0"
                                onClick={() => setLinhas((p) => p.filter((_, i) => i !== idx))}>
                                <i className="bi bi-trash" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!readOnly && !temRespostas && (
                  <div className="row g-2 align-items-end p-2 rounded" style={{ background: "var(--bg-highlight)" }}>
                    <div className="col-md-5">
                      <ItemEstoqueAutocomplete
                        listId="cotacao-item-list"
                        itens={itensEstoque}
                        value={novaLinha.idItem}
                        onChange={(idItem) => {
                          const um = umPadraoItem(itensEstoque, idItem);
                          setNovaLinha((p) => ({ ...p, idItem, idUnidadeInformada: um ? String(um) : "" }));
                        }}
                      />
                    </div>
                    <div className="col-2">
                      <input type="number" step="0.001" className="form-control form-control-sm"
                        placeholder="Qtd" value={novaLinha.quantidade}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, quantidade: e.target.value }))} />
                    </div>
                    <div className="col-2">
                      <select className="form-select form-select-sm" value={novaLinha.idUnidadeInformada}
                        disabled={!novaLinha.idItem}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, idUnidadeInformada: e.target.value }))}>
                        <option value="">UM</option>
                        {umsDoItem(unidades, itensEstoque, novaLinha.idItem).map((u) => (
                          <option key={u.id} value={u.id}>{u.sigla}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-2">
                      <input type="date" className="form-control form-control-sm"
                        value={novaLinha.prazoDesejado}
                        onChange={(e) => setNovaLinha((p) => ({ ...p, prazoDesejado: e.target.value }))} />
                    </div>
                    <div className="col-auto">
                      <button type="button" className="btn btn-sm btn-primary" onClick={addLinha}>
                        <i className="bi bi-plus-lg" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {aba === "fornecedores" && (
              <div>
                <p className="small text-muted mb-2">
                  Selecione os fornecedores convidados. O envio do e-mail é manual — use os botões abaixo após salvar.
                </p>
                {fornecedoresSemEmail.length > 0 && (
                  <div className="alert alert-warning py-2 small mb-3">
                    <i className="bi bi-exclamation-triangle me-1" />
                    <strong>{fornecedoresSemEmail.length} fornecedor(es) sem e-mail no cadastro:</strong>
                    {" "}
                    {fornecedoresSemEmail.map((f, i) => (
                      <span key={f.id}>
                        {i > 0 && ", "}
                        {canEditFornecedor && onNavigate ? (
                          <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                            onClick={() => abrirCadastroFornecedor(f.id)}>
                            {f.nmReduzido || f.nmRazaoSocial}
                          </button>
                        ) : (
                          f.nmReduzido || f.nmRazaoSocial
                        )}
                      </span>
                    ))}
                    . Atualize o cadastro para poder enviar o convite.
                  </div>
                )}
                {cotacaoId && canEdit && !readOnly && (cotacao?.fornecedores || []).length > 0 && (
                  <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                    <button type="button" className="btn btn-outline-primary btn-sm"
                      disabled={enviandoConvites || saving}
                      onClick={() => enviarConvites()}>
                      <i className="bi bi-envelope me-1" />
                      {enviandoConvites ? "Enviando…" : "Enviar convite para todos"}
                    </button>
                    {fornecedoresPendentes.length > 0 && (
                      <button type="button" className="btn btn-outline-warning btn-sm"
                        disabled={enviandoLembretes || saving}
                        onClick={() => enviarLembretes()}>
                        <i className="bi bi-bell me-1" />
                        {enviandoLembretes ? "Enviando…" : `Lembrete (${fornecedoresPendentes.length})`}
                      </button>
                    )}
                    <span className="small text-muted">
                      Convite = primeiro e-mail. Lembrete = quem ainda não respondeu (mantém o mesmo link).
                      Lembretes automáticos são enviados diariamente para convites pendentes.
                    </span>
                  </div>
                )}
                <div className="row g-1">
                  {fornecedores.map((f) => {
                    const sel = fornecedoresSel.includes(String(f.id));
                    const cf = (cotacao?.fornecedores || []).find(
                      (x) => String(x.idfornecedor || x.idFornecedor) === String(f.id)
                    );
                    const temEmail = !!emailCadastroFornecedor(f.id);
                    const dtEnvio = cf?.dtenvioemail || cf?.dtEnvioEmail;
                    const dtLembrete = cf?.dtenviolembrete || cf?.dtEnvioLembrete;
                    return (
                      <div key={f.id} className="col-md-6">
                        <div className={`border rounded p-2 small ${!temEmail && sel ? "border-warning" : ""}`}>
                          <label className="d-flex align-items-start gap-2 mb-0">
                            <input type="checkbox" className="form-check-input mt-1"
                              checked={sel} disabled={readOnly || (cf?.status === "Respondida")}
                              onChange={() => toggleFornecedor(f.id)} />
                            <span className="flex-grow-1">
                              <strong>{f.nmReduzido || f.nmRazaoSocial}</strong>
                              <br />
                              {temEmail ? (
                                <span className="text-muted">{f.email}</span>
                              ) : (
                                <span className="text-warning">
                                  <i className="bi bi-exclamation-circle me-1" />
                                  Sem e-mail no cadastro
                                  {canEditFornecedor && onNavigate && (
                                    <>
                                      {" — "}
                                      <button type="button" className="btn btn-link btn-sm p-0 align-baseline text-warning"
                                        onClick={() => abrirCadastroFornecedor(f.id)}>
                                        Cadastrar e-mail
                                      </button>
                                    </>
                                  )}
                                </span>
                              )}
                              {cf && (
                                <>
                                  <span className={`badge bg-${cf.status === "Respondida" ? "success" : "secondary"} ms-1`}>
                                    {cf.status}
                                  </span>
                                  {dtEnvio && (
                                    <span className="badge bg-info text-dark ms-1" title="Convite enviado">
                                      <i className="bi bi-envelope-check me-1" />
                                      {fmtDtEnvio(dtEnvio)}
                                    </span>
                                  )}
                                  {dtLembrete && (
                                    <span className="badge bg-warning text-dark ms-1" title="Lembrete enviado">
                                      <i className="bi bi-bell me-1" />
                                      {fmtDtEnvio(dtLembrete)}
                                    </span>
                                  )}
                                </>
                              )}
                            </span>
                          </label>
                          {cotacaoId && cf && canEdit && !readOnly && temEmail && cf.status !== "Respondida" && (
                            <div className="mt-1">
                              <button type="button" className="btn btn-link btn-sm p-0 me-2"
                                disabled={enviandoConvites || saving}
                                onClick={() => enviarConvites(cf.id)}>
                                <i className="bi bi-send me-1" />
                                {dtEnvio ? "Reenviar convite" : "Enviar convite"}
                              </button>
                              {cf.status === "Aguardando" && dtEnvio && (
                                <button type="button" className="btn btn-link btn-sm p-0"
                                  disabled={enviandoLembretes || saving}
                                  onClick={() => enviarLembretes(cf.id)}>
                                  <i className="bi bi-bell me-1" />
                                  Enviar lembrete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {aba === "respostas" && cotacaoId && (
              <div>
                <p className="small text-muted mb-2">
                  Registre manualmente a proposta ou aguarde o fornecedor responder pelo link enviado por e-mail.
                </p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {(cotacao?.fornecedores || []).map((f) => (
                    <button key={f.id} type="button"
                      className={`btn btn-sm ${fornRespId === String(f.id) ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => abrirResposta(f.id)}>
                      {f.nmfornecedor || f.nmFornecedor}
                      <span className={`badge ms-1 bg-${f.status === "Respondida" ? "success" : "secondary"}`}>
                        {f.status}
                      </span>
                    </button>
                  ))}
                </div>
                {fornRespId && (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Item</th><th>Qtd</th>
                            <th className="text-end">Preço un.</th><th>Entrega</th><th>Obs.</th><th>Disp.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {respLinhas.map((l, idx) => (
                            <tr key={l.idCotacaoItem}>
                              <td><code className="small">{l.cdItem}</code> {l.nmItem}</td>
                              <td>{fmtQtd(l.quantidade)} {l.siglaUm}</td>
                              <td>
                                <input type="number" step="0.0001" className="form-control form-control-sm text-end"
                                  disabled={!l.disponivel || readOnly} value={l.vlrUnitario}
                                  onChange={(e) => setRespLinhas((p) => p.map((x, i) => (
                                    i === idx ? { ...x, vlrUnitario: e.target.value } : x
                                  )))} />
                              </td>
                              <td>
                                <input type="date" className="form-control form-control-sm"
                                  disabled={!l.disponivel || readOnly} value={l.prazoEntrega}
                                  onChange={(e) => setRespLinhas((p) => p.map((x, i) => (
                                    i === idx ? { ...x, prazoEntrega: e.target.value } : x
                                  )))} />
                              </td>
                              <td>
                                <input className="form-control form-control-sm" disabled={!l.disponivel || readOnly}
                                  value={l.observacao}
                                  onChange={(e) => setRespLinhas((p) => p.map((x, i) => (
                                    i === idx ? { ...x, observacao: e.target.value } : x
                                  )))} />
                              </td>
                              <td className="text-center">
                                <input type="checkbox" className="form-check-input" disabled={readOnly}
                                  checked={!!l.disponivel}
                                  onChange={(e) => setRespLinhas((p) => p.map((x, i) => (
                                    i === idx ? { ...x, disponivel: e.target.checked } : x
                                  )))} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {canEdit && !readOnly && (
                      <button type="button" className="btn btn-primary btn-sm" disabled={saving}
                        onClick={salvarResposta}>
                        {saving ? "Salvando…" : "Salvar resposta deste fornecedor"}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {aba === "mapa" && cotacaoId && (
              <div>
                {pedidosGerados.length > 0 && (
                  <div className="alert alert-info py-2 small mb-3">
                    <i className="bi bi-bag-check me-1" />
                    Pedidos já gerados desta cotação:
                    {" "}
                    {pedidosGerados.map((p, i) => (
                      <span key={p.id}>
                        {i > 0 && ", "}
                        <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                          onClick={() => abrirPedido(p.id)}>
                          {p.nrPedido}
                        </button>
                        <span className="text-muted"> ({p.qtdItens} it.)</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button type="button" className="btn btn-outline-secondary btn-sm"
                    disabled={imprimindoMapa || !mapa}
                    onClick={imprimirMapaPdf}>
                    <i className="bi bi-printer me-1" />
                    {imprimindoMapa ? "Gerando…" : "Imprimir / PDF"}
                  </button>
                {!mapaBloqueado && canEdit && (
                  <>
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                      disabled={saving || gerandoPedidos} onClick={aplicarMenorPreco}>
                      <i className="bi bi-sort-numeric-down me-1" />
                      Menor preço em todos
                    </button>
                    <button type="button" className="btn btn-outline-primary btn-sm"
                      disabled={saving || gerandoPedidos || !qtdEscolhas}
                      onClick={salvarEscolhasMapa}>
                      Salvar escolhas
                    </button>
                    <button type="button" className="btn btn-primary btn-sm"
                      disabled={gerandoPedidos || saving || !escolhasCompletas}
                      onClick={gerarPedidos}>
                      <i className="bi bi-bag-plus me-1" />
                      {gerandoPedidos ? "Gerando…" : "Gerar pedido(s)"}
                    </button>
                    {!escolhasCompletas && qtdItensMapa > 0 && (
                      <span className="small text-muted align-self-center">
                        Selecione vencedor em {qtdItensMapa - qtdEscolhas} item(ns).
                      </span>
                    )}
                  </>
                )}
                </div>
                <div className="table-responsive">
                  {!mapa && <p className="text-muted small">Carregando mapa…</p>}
                  {mapa && (
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Item</th><th>Qtd</th>
                          {(mapa.fornecedores || []).map((f) => (
                            <th key={f.id} className="text-end small">{f.nmFornecedor}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(mapa.itens || []).map((it) => (
                          <tr key={it.id}>
                            <td><code className="small">{it.cdItem}</code> {it.nmItem}</td>
                            <td>{fmtQtd(it.quantidade)} {it.siglaUm}</td>
                            {(mapa.fornecedores || []).map((f) => {
                              const c = celulaMapa(it.id, f.id);
                              const menor = menoresPorItem[it.id];
                              const isMin = c && menor != null && c.vlrUnitario === menor;
                              const sel = escolhas[it.id] === String(f.id);
                              return (
                                <td key={f.id}
                                  className={`text-end small ${isMin ? "table-success" : ""} ${sel ? "table-primary" : ""}`}>
                                  {c ? (
                                    <label className="d-block mb-0" style={{ cursor: mapaBloqueado ? "default" : "pointer" }}>
                                      {!mapaBloqueado && (
                                        <input type="radio" className="form-check-input me-1"
                                          name={`esc-${it.id}`}
                                          checked={sel}
                                          onChange={() => setEscolhas((p) => ({
                                            ...p, [it.id]: String(f.id),
                                          }))} />
                                      )}
                                      <div>{fmtMoeda(c.vlrUnitario)}</div>
                                      {c.prazoEntrega && (
                                        <div className="text-muted">{c.prazoEntrega.split("T")[0]}</div>
                                      )}
                                    </label>
                                  ) : "—"}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {previewPedidos.length > 0 && !pedidosGerados.length && (
                  <div className="mt-3 p-2 rounded small" style={{ background: "var(--bg-highlight)" }}>
                    <strong>Prévia dos pedidos ({previewPedidos.length}):</strong>
                    <ul className="mb-0 mt-1">
                      {previewPedidos.map((g) => (
                        <li key={g.idCotFornecedor}>
                          <strong>{g.nmFornecedor}</strong>
                          {" — "}
                          {g.itens.length} item(ns), total estimado {fmtMoeda(g.total)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="small text-muted mb-0 mt-2">
                  Verde = menor preço. Azul = vencedor selecionado. Um pedido em rascunho por fornecedor vencedor.
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Fechar</button>
            {canEdit && !readOnly && aba !== "mapa" && (
              <button type="button" className="btn btn-primary" disabled={saving} onClick={salvar}>
                {saving ? "Salvando…" : "Salvar"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
